import {PropSchema} from 'serializr';
import {dateToIsoDateString, dateToIsoDateTimeString, dateToIsoTimeString, isoDateStringToDate, isoDateTimeStringToDate, isoTimeStringToDate} from '../dates/IsoDateConversions';
import {LocalDate, LocalDateTime, LocalTime} from '@js-joda/core';
import {MobxDoneCallback} from './MobxDoneCallback';
import {namedClassWithParseFnDeserializer} from './NamedClassWithParseFnDeserializer';
import {toStringSerializer} from './ToStringSerializer';

/**
 * Types passed as arguments to serializable decorator.
 */

/**
 * Pass this function as argument to serializable decorator to convert property to Date object
 * during deserialization and back to ISO date string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(isoDate())
 *    property: Date;
 * }
 * ```
 */
const isoDate = (): PropSchema => ({
   serializer: (sourcePropertyValue: Date) => dateToIsoDateString(sourcePropertyValue),
   deserializer: (jsonValue: string, done: MobxDoneCallback) => done(null, isoDateStringToDate(jsonValue))
});

/**
 * Pass this function as argument to serializable decorator to convert property to Date object
 * during deserialization and back to ISO date time string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(isoDateTime())
 *    property: Date;
 * }
 * ```
 */
const isoDateTime = (): PropSchema => ({
   serializer: (sourcePropertyValue: Date) => dateToIsoDateTimeString(sourcePropertyValue),
   deserializer: (jsonValue: string, done: MobxDoneCallback) => done(null, isoDateTimeStringToDate(jsonValue))
});

/**
 * Pass this function as argument to serializable decorator to convert property to Date object
 * during deserialization and back to ISO time string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(isoTime())
 *    property: Date;
 * }
 * ```
 */
const isoTime = (): PropSchema => ({
   serializer: (sourcePropertyValue: Date) => dateToIsoTimeString(sourcePropertyValue),
   deserializer: (jsonValue: string, done: MobxDoneCallback) => done(null, isoTimeStringToDate(jsonValue))
});

/**
 * Pass this function as argument to serializable decorator to convert property to decimal
 * during deserialization. Serialization returns also decimal.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(decimal())
 *    property: string;
 * }
 * ```
 */
const decimal = (): PropSchema => ({
   serializer: (sourcePropertyValue: number): number => sourcePropertyValue,
   deserializer: decimalDeserializer
});

const decimalDeserializer = (jsonValue: string, done: MobxDoneCallback) => {
   const value = parseFloat(jsonValue);
   const errorMessage = isNaN(value) ? `'${jsonValue}' is not a valid number` : null;
   return done(errorMessage, value);
};

/**
 * Pass this function as argument to serializable decorator to convert property to integer
 * during deserialization. Serialization returns also integer.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(integer())
 *    property: string;
 * }
 * ```
 */
const integer = (): PropSchema => ({
   serializer: (sourcePropertyValue: number): number => sourcePropertyValue,
   deserializer: integerDeserializer
});

const integerDeserializer = (jsonValue: string, done: MobxDoneCallback) => {
   const value = parseFloat(jsonValue);
   const errorMessage = Number.isSafeInteger(value) ? null : `'${jsonValue}' is not a valid integer`;
   return done(errorMessage, value);
};

/**
 * Pass this function as argument to serializable decorator to convert property to a LocalDate object
 * during deserialization and back to a LocalDate string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(localDate())
 *    property: LocalDate;
 * }
 * ```
 */
const localDate = (): PropSchema => ({
   serializer: toStringSerializer,
   deserializer: namedClassWithParseFnDeserializer(LocalDate)
});

/**
 * Pass this function as argument to serializable decorator to convert property to a LocalDateTime object
 * during deserialization and back to a LocalDateTime string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(localDateTime())
 *    property: LocalDateTime;
 * }
 * ```
 */
const localDateTime = (): PropSchema => ({
   serializer: toStringSerializer,
   deserializer: namedClassWithParseFnDeserializer(LocalDateTime)
});

/**
 * Pass this function as argument to serializable decorator to convert property to a LocalTime object
 * during deserialization and back to a LocalTime string during serialization.
 *
 * ```typescript
 * class TestModel {
 *    @serializable(localTime())
 *    property: LocalTime;
 * }
 * ```
 */
const localTime = (): PropSchema => ({
   serializer: toStringSerializer,
   deserializer: namedClassWithParseFnDeserializer(LocalTime)
});

export { isoDate, isoDateTime, isoTime, decimal, integer, localDate, localDateTime, localTime };
